/**
 * CUSTOMER-172A / CUSTOMER-R4.15 / R7.2.2C — server search client.
 * Never scans full catalog on device. Fail-open: valid no-result ≠ technical error.
 */
import type { SearchSuggestion } from "./search-v2.js";
import { formatUah } from "./money-v2.js";
import {
  deriveSearchOutcome,
  isLikelyBarcodeQuery,
  normalizeBarcodeDigits,
  type SearchOutcomeKind,
} from "./search-outcome-v2.js";

/** Canonical scope for Home / New Arrivals / category / brand / country / Search tab. */
export const SEARCH_SCOPE_GLOBAL_CATALOG = "GLOBAL_CATALOG";

export type ServerSearchHit = {
  id: string;
  title: string;
  brand: string | null;
  thumbnail: string | null;
  availability: string;
  price: number | null;
  match_reason: string;
  type: "product";
};

export type ServerSearchResponse = {
  ok: boolean;
  outcome?: SearchOutcomeKind;
  catalog_revision?: string;
  lexicon_revision?: string;
  search_index_revision?: string;
  query?: string;
  correction?: string | null;
  correction_kind?: string | null;
  did_you_mean?: string | null;
  total?: number;
  has_more?: boolean;
  hits?: ServerSearchHit[];
  error?: string;
  search_source?: string;
  technical_error?: boolean;
};

async function fetchJson(url: string, signal?: AbortSignal): Promise<ServerSearchResponse> {
  const init: RequestInit = {
    method: "GET",
    headers: { Accept: "application/json" },
  };
  if (signal) init.signal = signal;
  try {
    const res = await fetch(url, init);
    const ctype = res.headers.get("content-type") ?? "";
    const text = await res.text();
    if (!ctype.includes("json")) {
      return {
        ok: false,
        outcome: "TEMPORARY_TECHNICAL_ERROR",
        technical_error: true,
        error: `non_json_${res.status}`,
      };
    }
    let body: ServerSearchResponse;
    try {
      body = JSON.parse(text) as ServerSearchResponse;
    } catch {
      return {
        ok: false,
        outcome: "TEMPORARY_TECHNICAL_ERROR",
        technical_error: true,
        error: "invalid_json",
      };
    }
    if (!res.ok || body.ok === false) {
      return {
        ...body,
        ok: false,
        outcome: "TEMPORARY_TECHNICAL_ERROR",
        technical_error: true,
        error: body.error || `http_${res.status}`,
      };
    }
    const outcome = body.outcome ?? deriveSearchOutcome(body);
    return { ...body, ok: true, outcome, technical_error: false };
  } catch (err) {
    const name = err && typeof err === "object" && "name" in err ? String((err as { name: string }).name) : "";
    if (name === "AbortError") throw err;
    return {
      ok: false,
      outcome: "TEMPORARY_TECHNICAL_ERROR",
      technical_error: true,
      error: "network_or_throw",
    };
  }
}

function workerSearchUrl(
  query: string,
  limit: number,
  offset: number,
  path: "search" | "suggest" = "search",
): string | null {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim()?.replace(/\/$/, "");
  if (!base) return null;
  const params = new URLSearchParams({
    q: query,
    limit: String(limit),
  });
  if (path === "search") params.set("offset", String(offset));
  const suffix = path === "suggest" ? "/api/miniapp/store/search/suggest" : "/api/miniapp/store/search";
  return `${base}${suffix}?${params.toString()}`;
}

async function lookupBarcodeHit(code: string, signal?: AbortSignal): Promise<ServerSearchHit | null> {
  const digits = normalizeBarcodeDigits(code);
  if (digits.length < 8) return null;
  try {
    const init: RequestInit = {
      method: "GET",
      headers: { Accept: "application/json" },
    };
    if (signal) init.signal = signal;
    const res = await fetch(`/api/store-v2/barcode/${encodeURIComponent(digits)}`, init);
    if (!res.ok) return null;
    const body = (await res.json()) as {
      ok?: boolean;
      product_id?: string;
      name?: string;
      thumbnail?: string | null;
      price?: number | null;
      availability?: string | null;
      brand?: string | null;
    };
    if (!body.ok || !body.product_id) return null;
    return {
      id: body.product_id,
      title: body.name ?? body.product_id,
      brand: body.brand ?? null,
      thumbnail: body.thumbnail ?? null,
      availability: body.availability ?? "unknown",
      price: typeof body.price === "number" ? body.price : null,
      match_reason: "exact_barcode_or_sku",
      type: "product",
    };
  } catch {
    return null;
  }
}

/** Stateless suggest — no module globals. Caller owns AbortSignal. */
export async function serverSuggest(
  query: string,
  limit = 8,
  signal?: AbortSignal,
): Promise<ServerSearchResponse> {
  const q = query.trim();
  if (q.length < 2) {
    return { ok: true, outcome: "TRUE_NO_RESULT", hits: [], total: 0, has_more: false, technical_error: false };
  }
  const url = `/api/store-v2/search/suggest?q=${encodeURIComponent(q)}&limit=${Math.min(limit, 8)}&search_scope=${SEARCH_SCOPE_GLOBAL_CATALOG}`;
  let result = await fetchJson(url, signal);
  if (result.technical_error) {
    const workerUrl = workerSearchUrl(q, limit, 0, "suggest");
    if (workerUrl) {
      const workerResult = await fetchJson(workerUrl, signal);
      if (!workerResult.technical_error) {
        result = { ...workerResult, search_source: workerResult.search_source ?? "worker_direct" };
      }
    }
  }
  return result;
}

/** Stateless search — Pages first, Worker fallback, barcode-first for numeric queries. */
export async function serverSearch(
  query: string,
  opts: { limit?: number; offset?: number; signal?: AbortSignal } = {},
): Promise<ServerSearchResponse> {
  const q = query.trim();
  if (!q) {
    return { ok: true, outcome: "TRUE_NO_RESULT", hits: [], total: 0, has_more: false, technical_error: false };
  }
  const limit = Math.min(Math.max(opts.limit ?? 24, 1), 48);
  const offset = Math.max(opts.offset ?? 0, 0);

  if (isLikelyBarcodeQuery(q) && offset === 0) {
    const barcodeHit = await lookupBarcodeHit(q, opts.signal);
    if (barcodeHit) {
      return {
        ok: true,
        outcome: "RESULTS",
        query: q,
        total: 1,
        has_more: false,
        hits: [barcodeHit],
        technical_error: false,
        search_source: "barcode_registry",
      };
    }
  }

  const pagesUrl = `/api/store-v2/search?q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}&search_scope=${SEARCH_SCOPE_GLOBAL_CATALOG}`;
  let result = await fetchJson(pagesUrl, opts.signal);
  if (result.technical_error) {
    const workerUrl = workerSearchUrl(q, limit, offset);
    if (workerUrl) {
      const workerResult = await fetchJson(workerUrl, opts.signal);
      if (!workerResult.technical_error) {
        result = { ...workerResult, search_source: workerResult.search_source ?? "worker_direct" };
      }
    }
  }
  return result;
}

export function hitsToSuggestions(hits: ServerSearchHit[]): SearchSuggestion[] {
  return hits.slice(0, 5).map((h) => ({
    kind: "product" as const,
    label: h.title,
    query: h.title,
    productId: h.id,
    brand: h.brand,
    imageUrl: h.thumbnail,
    priceUah: h.price,
    matchReason: h.match_reason,
  }));
}

/** Minimal catalog card from server hit — no full-catalog client scan. */
export function hitToCatalogProduct(hit: ServerSearchHit): import("./catalog-v2-types.js").CatalogV2Product {
  const sellable = hit.availability !== "out_of_stock";
  const catalogPrice = hit.price;
  return {
    id: hit.id,
    sku: "",
    title: hit.title,
    titleRu: null,
    categoryKeys: [],
    categoryName: "",
    imageUrl: hit.thumbnail,
    productUrl: null,
    priceUah: catalogPrice ?? 0,
    displayPrice: catalogPrice != null ? formatUah(catalogPrice) : "",
    isPriced: catalogPrice != null && catalogPrice > 0,
    stockStatus: hit.availability || "unknown",
    isSellable: sellable,
    sellabilityReason: sellable ? "ok" : "out_of_stock",
    brand: hit.brand,
    country: null,
    weight: null,
    regularPriceUah: null,
    salePriceUah: null,
    saleStartsAt: null,
    saleEndsAt: null,
    promotionId: null,
    promotionStatus: null,
    priceUpdatedAt: null,
    sourceRevision: null,
    search: "",
    promProductId: hit.id,
    merchantSku: null,
    merchantArticle: null,
    barcodeGtin: null,
    barcodeVerification: "absent",
    manufacturerPartNumber: null,
    warehouseCode: null,
    sourceSystem: "server_search_v1",
    sourceUpdatedAt: null,
    lastSyncedAt: null,
    firstSeenAt: null,
    catalogArrivedAt: null,
    sourceTitle: hit.title,
    customerTitle: hit.title,
    titleAliases: [],
    canaryBadge: null,
  };
}
